import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button, Card, Container } from "react-bootstrap";
import DataTable from "../../components/DataTable";
import useAccess from "../../hooks/useAccess";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("en-US");
};

const DriverAbstractList = () => {
  const navigate = useNavigate();
  const { id: paramId } = useParams();

  const role = localStorage.getItem("userRole");
  const isAdmin = role === "1";
  const { hasPermission } = useAccess();
  const loggedInId = localStorage.getItem("userId");
  const userId = isAdmin && paramId ? paramId : loggedInId;
  const canDeleteRecords = isAdmin && hasPermission("Task", "Task", "delete");

  const [records, setRecords] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/driverabstract/${userId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        setRecords(res.data || []);
      } catch (err) {
        console.error("Error fetching driver's abstracts", err);
      }
    };

    if (userId) fetchData();
  }, [userId]);

  const handleDeleteRecord = async (recordId) => {
    if (!canDeleteRecords || !recordId) return;
    if (!window.confirm("Delete this record? This cannot be undone.")) return;

    try {
      await axios.delete(`${API_BASE}/api/driverabstract/${recordId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setRecords((prev) => prev.filter((row) => row.da_id !== recordId));
    } catch (err) {
      console.error("Driver abstract delete failed", err);
      alert(err?.response?.data?.message || "Failed to delete record.");
    }
  };

  const columns = [
    {
      key: "rowNumber",
      label: "#",
      width: "70px",
      getSortValue: (row) => row.da_id,
      render: (_row, index) => index + 1,
    },
    {
      key: "da_updated_date",
      label: "Date Saved",
      render: (row) => formatDate(row.da_updated_date),
    },
    {
      key: "saved_by",
      label: "Saved By",
      render: (row) => row.saved_by || "-",
    },
    {
      key: "da_created_date",
      label: "Date Created",
      render: (row) => formatDate(row.da_created_date),
    },
    {
      key: "created_by",
      label: "Created By",
      render: (row) => row.created_by || "-",
    },
    {
      key: "da_date_issued",
      label: "Date Issued",
      getSortValue: (row) => row.da_date_issued || "",
      render: (row) => formatDate(row.da_date_issued),
    },
    {
      key: "da_medical_expiration",
      label: "Medical Expiration",
      render: (row) => formatDate(row.da_medical_expiration),
    },
    {
      key: "da_points_type",
      label: "Points Type",
      render: (row) => row.da_points_type || "-",
    },
    {
      key: "da_points",
      label: "Points",
      headerClassName: "text-center",
      cellClassName: "text-center",
      render: (row) => row.da_points ?? 0,
    },
    {
      key: "da_no_violations",
      label: "No Violations",
      headerClassName: "text-center",
      cellClassName: "text-center",
      render: (row) => row.da_no_violations || "-",
    },
    {
      key: "file_count",
      label: "Files",
      headerClassName: "text-center",
      cellClassName: "text-center",
      render: (row) => row.file_count || 0,
    },
    {
      key: "actions",
      label: "Action",
      sortable: false,
      headerClassName: "text-center driver-abstract-action-column",
      cellClassName: "text-center driver-abstract-action-column",
      width: canDeleteRecords ? "112px" : "70px",
      render: (row) => (
        <div className="d-flex gap-2 justify-content-center">
          <button
            type="button"
            className="app-table-action-button is-edit"
            onClick={() => navigate(`/driverabstract/${userId}/detail/${row.da_id}`)}
            title="Edit record"
            aria-label="Edit record"
          >
            <i className="fas fa-edit" aria-hidden="true" />
          </button>
          {canDeleteRecords && (
            <button
              type="button"
              className="app-table-action-button is-delete"
              onClick={() => handleDeleteRecord(row.da_id)}
              title="Delete record"
              aria-label="Delete record"
            >
              <i className="fas fa-trash" aria-hidden="true" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <Container className="form-wrapper my-4 print-area" data-print-record-list>
      <div className="d-none" data-download-exclude>
        {records.map((record) => (
          <span key={`print-${record.da_id}`} data-print-record-path={`/driverabstract/${userId}/detail/${record.da_id}`} />
        ))}
      </div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="mb-0">Driver&apos;s Abstract</h3>
        <div>
          <Button
            variant="secondary"
            className="me-2"
            onClick={() =>
              navigate(isAdmin && paramId ? `/userhome/${paramId}` : "/userhome")
            }
          >
            Return
          </Button>
          <Button
            variant="success"
            onClick={() => navigate(`/driverabstract/${userId}/new`)}
          >
            Add
          </Button>
        </div>
      </div>

      <Card>
        <Card.Body>
          {records.length === 0 ? (
            <div className="text-muted text-center">
              No driver&apos;s abstracts found.
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={records}
              rowKey="da_id"
              itemsPerPage={10}
              initialSortKey="da_date_issued"
              initialSortDirection="desc"
              tableClassName="driver-abstract-table"
              emptyMessage="No driver's abstracts found."
            />
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default DriverAbstractList;
